import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Paper,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Avatar,
  Divider,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Alert,
  Skeleton,
} from '@mui/material';
// Using new design system components
import { Button, Typography, Card } from '../../../design-system';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';
import {
  Pets,
  Agriculture,
  LocalDining,
  Build,
  Medication,
  Category,
  FilterList,
  Search,
  Visibility,
  AttachMoney,
  LocationOn,
  Schedule,
} from '@mui/icons-material';
import { 
  SalesLot, 
  MarketplaceSearchResult, 
  MarketplaceSearchFilters,
  ItemType,
  itemTypeLabels,
  lotStatusLabels,
  lotStatusColors
} from '../types/marketplaceTypes';

interface AvailableLotsListProps {
  searchResult: MarketplaceSearchResult;
  onLotSelect: (lotId: string) => void;
  onFiltersChange?: (filters: MarketplaceSearchFilters) => void;
}

const getItemTypeIcon = (itemType: ItemType) => {
  const iconMap = {
    animal: <Pets />,
    fertilizer: <Agriculture />,
    nutrition: <LocalDining />,
    equipment: <Build />,
    feed: <LocalDining />,
    medicine: <Medication />,
    other: <Category />
  };
  return iconMap[itemType] || <Category />;
};

export const AvailableLotsList: React.FC<AvailableLotsListProps> = ({
  searchResult,
  onLotSelect,
  onFiltersChange,
}) => {
  const { t } = useTranslation('common');
  const [showFilters, setShowFilters] = useState(false);
  const [localFilters, setLocalFilters] = useState<MarketplaceSearchFilters>(searchResult.filters);

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'BRL') {
      return `R$ ${amount.toLocaleString('pt-BR')}`;
    }
    return `${currency} ${amount.toLocaleString()}`;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const handleFilterChange = (key: keyof MarketplaceSearchFilters, value: any) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const clearFilters = () => {
    const newFilters: MarketplaceSearchFilters = {};
    setLocalFilters(newFilters);
    if (onFiltersChange) {
      onFiltersChange(newFilters);
    }
  };

  const renderLotCard = (lot: SalesLot) => (
    <Card 
      key={lot.id}
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        }
      }}
      onClick={() => onLotSelect(lot.id)}
    >
      <CardContent sx={{ flexGrow: 1, p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              bgcolor: DESIGN_TOKENS.colors.primary[700],
              mr: 2,
              width: 48,
              height: 48,
            }}
          >
            {getItemTypeIcon(lot.itemType)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
              {lot.title}
            </Typography>
            <Typography variant="body2" color="secondary">
              {lot.organizationName}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <Chip
              label={lotStatusLabels[lot.status]}
              size="small"
              sx={{
                bgcolor: lotStatusColors[lot.status],
                color: 'white',
                fontWeight: 600,
                mb: 0.5,
              }}
            />
            {lot.isUrgent && (
              <Chip
                label="URGENTE"
                size="small"
                sx={{
                  bgcolor: DESIGN_TOKENS.colors.status.error[500],
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )}
          </Box>
        </Box>

        {/* Price and Quantity */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.status.success[500] }}>
              {formatCurrency(lot.pricePerItem, lot.currency)}
            </Typography>
            <Typography variant="caption" color="secondary">
              por item
            </Typography>
          </Box>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {lot.items.length}
            </Typography>
            <Typography variant="caption" color="secondary">
              {lot.items.length === 1 ? 'item' : 'itens'}
            </Typography>
          </Box>
        </Box>

        {/* Description */}
        {lot.description && (
          <Typography 
            variant="body2" 
            color="secondary" 
            sx={{ 
              mb: 2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {lot.description}
          </Typography>
        )}

        {/* Tags */}
        {lot.tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {lot.tags.slice(0, 3).map((tag, index) => (
              <Chip
                key={index}
                label={tag}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            ))}
            {lot.tags.length > 3 && (
              <Chip
                label={`+${lot.tags.length - 3}`}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem' }}
              />
            )}
          </Box>
        )}

        {/* Additional Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 'auto' }}>
          {lot.location && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <LocationOn sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
              <Typography variant="caption" color="secondary">
                  {lot.location}
                </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Visibility sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.text.secondary }} />
            <Typography variant="caption" color="secondary">
                {lot.viewCount} visualizações
              </Typography>
          </Box>
        </Box>
      </CardContent>

      <Divider />

      <CardActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Typography variant="caption" color="secondary">
              {formatDate(lot.createdAt)}
            </Typography>
        <Button
          variant="primary"
          size="small"
          sx={{
            bgcolor: DESIGN_TOKENS.colors.status.success[500],
            '&:hover': { bgcolor: DESIGN_TOKENS.colors.status.success[600] },
          }}
        >
          Ver Detalhes
        </Button>
      </CardActions>
    </Card>
  );

  const renderFilters = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {t('marketplace.filters.title')}
        </Typography>
        <Button
          variant="secondary"
          size="small"
          onClick={clearFilters}
          sx={{ color: DESIGN_TOKENS.colors.status.success[500] }}
        >
          {t('marketplace.filters.clearFilters')}
        </Button>
      </Box>

             <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
         <FormControl fullWidth size="small">
           <InputLabel>{t('marketplace.filters.itemType')}</InputLabel>
           <Select
             value={localFilters.itemType || ''}
             label={t('marketplace.filters.itemType')}
             onChange={(e) => handleFilterChange('itemType', e.target.value || undefined)}
           >
             <MenuItem value="">{t('marketplace.filters.all')}</MenuItem>
             {Object.entries(itemTypeLabels).map(([key, label]) => (
               <MenuItem key={key} value={key}>
                 {label}
               </MenuItem>
             ))}
           </Select>
         </FormControl>

         <TextField
           fullWidth
           size="small"
           label={t('marketplace.filters.minPrice')}
           type="number"
           value={localFilters.minPrice || ''}
           onChange={(e) => handleFilterChange('minPrice', e.target.value ? Number(e.target.value) : undefined)}
           InputProps={{
             startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>R$</Typography>,
           }}
         />

         <TextField
           fullWidth
           size="small"
           label={t('marketplace.filters.maxPrice')}
           type="number"
           value={localFilters.maxPrice || ''}
           onChange={(e) => handleFilterChange('maxPrice', e.target.value ? Number(e.target.value) : undefined)}
           InputProps={{
             startAdornment: <Typography variant="body2" sx={{ mr: 1 }}>R$</Typography>,
           }}
         />

         <TextField
           fullWidth
           size="small"
           label={t('marketplace.filters.location')}
           value={localFilters.location || ''}
           onChange={(e) => handleFilterChange('location', e.target.value || undefined)}
           placeholder="Ex: São Paulo"
         />
       </Box>
    </Paper>
  );

  return (
    <Box sx={{ height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[700]} 0%, ${DESIGN_TOKENS.colors.status.success[500]} 100%)`,
          color: 'white',
          p: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Marketplace
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {searchResult.totalCount} {searchResult.totalCount === 1 ? t('marketplace.searchResults.lotFound') : t('marketplace.searchResults.lotsFound')}
            </Typography>
          </Box>
          <IconButton
            sx={{ color: 'white' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            <FilterList />
          </IconButton>
        </Box>

        {/* Summary chips */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {localFilters.itemType && (
            <Chip
              label={`Tipo: ${itemTypeLabels[localFilters.itemType]}`}
              size="small"
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
            />
          )}
          {(localFilters.minPrice || localFilters.maxPrice) && (
            <Chip
              label={`Preço: ${localFilters.minPrice ? `R$ ${localFilters.minPrice}` : '0'} - ${localFilters.maxPrice ? `R$ ${localFilters.maxPrice}` : '∞'}`}
              size="small"
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
            />
          )}
          {localFilters.location && (
            <Chip
              label={`Local: ${localFilters.location}`}
              size="small"
              sx={{ bgcolor: 'rgba(255, 255, 255, 0.2)', color: 'white' }}
            />
          )}
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* Filters */}
        {showFilters && renderFilters()}

        {/* Results */}
        {searchResult.lots.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="secondary" sx={{ mb: 2 }}>
              {t('marketplace.searchResults.noLotsFound')}
            </Typography>
            <Typography variant="body2" color="secondary">
              {t('marketplace.searchResults.tryAdjustFilters')}
            </Typography>
          </Box>
                 ) : (
           <Box sx={{ 
             display: 'grid', 
             gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', 
             gap: 3 
           }}>
             {searchResult.lots.map((lot) => (
               renderLotCard(lot)
             ))}
           </Box>
         )}
      </Box>
    </Box>
  );
};